// ===================== Game — simulation core =====================
import { CONFIG } from '../config.js';
import { RNG } from '../engine/rng.js';
import { Pool } from '../engine/pool.js';
import { SpatialHash } from '../engine/spatialhash.js';
import { Audio } from '../engine/audio.js';
import { Platform } from '../engine/platform.js';
import { Settings } from '../engine/settings.js';
import { clamp, dist, dist2, norm, angleOf, TAU, approachAngle } from '../engine/math.js';
import { Player } from './player.js';
import { getEnemy, getBoss, getElement, Weapons, LiveAchievements } from './registries.js';
import { getStage } from './stages.js';
import { renderScene } from './render.js';

export class Game {
  constructor(canvas, camera, input) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.camera = camera;
    this.input = input;
    this.rng = new RNG(12345);

    // The factory runs resetEnemy too, so a brand-new pool object and a
    // recycled one start from exactly the same state.
    this.enemies = new Pool(() => resetEnemy({}), (e) => resetEnemy(e));
    this.projectiles = new Pool(() => ({}), (p) => resetProjectile(p));
    this.ebullets = new Pool(() => ({}), (b) => { b.hitPlayer = false; });
    this.pickups = new Pool(() => ({}), (g) => { g.magnetized = false; });
    this.particles = new Pool(() => ({}), () => {});

    this.hash = new SpatialHash(88);
    // Persistent scratch arrays for the collision loops. They iterate query
    // results while hit effects (element onHit, chain lightning) can re-query
    // the hash — using the hash's shared buffer there would clobber the list
    // mid-loop. Own arrays, reused every frame: zero allocation, no aliasing.
    this._collideBuf = [];
    this._meleeBuf = [];
    this.state = 'menu';
    this._enemyId = 1;
    this._runCounter = 0;
    this.onGameOver = null;      // callback(summary)
    this.onAchievement = null;   // callback(achievementId) — main persists + toasts
    this.onOfferRevive = null;   // callback() — set only when a rewarded ad is available
    this.onOfferOverrun = null;  // callback() — offer to stay past a won finale (OVERRUN)
  }

  // Fire a live achievement (idempotent — main ignores already-unlocked ones).
  unlockAch(id) { if (this.onAchievement) this.onAchievement(id); }

  // Live achievements, checked cheaply each frame during play. The RULES are
  // authored in the content layer (defineLiveAchievement in
  // content/achievements.js) — the engine only walks them and fires whatever
  // id each one names, exactly as a boss def names its own `killAch`.
  _checkLiveAchievements() {
    for (const rule of LiveAchievements) {
      if (rule.test(this)) this.unlockAch(rule.id);
    }
  }

  // ---------------------------------------------------------------- lifecycle
  // `runConfig` is the world-map node's contract for this run (length,
  // difficulty, payout, boss cadence). Omit it and the stage plays at its own
  // baseline, exactly as it did before the map existed.
  start(charDef, metaBonuses, stageId, runConfig = null) {
    // Fresh entropy per run — captured ONCE here at run start, which is not a
    // sim wall-clock read (the sim only ever consumes the seeded stream).
    // Kept on `this.seed` so a run can be reproduced by reseeding with it.
    this._runCounter++;
    let h = 0;
    for (let i = 0; i < charDef.id.length; i++) h = Math.imul(h ^ charDef.id.charCodeAt(i), 2654435761);
    this.seed = (h ^ Date.now() ^ Math.imul(this._runCounter, 0x9e3779b9)) >>> 0;
    // A contract-supplied seed (the Weekly Gauntlet's shared horde) replaces
    // the entropy seed outright — same code path, same determinism.
    if (runConfig && runConfig.seed != null) this.seed = runConfig.seed >>> 0;
    this.rng = new RNG(this.seed);
    // A world-map hex lends its TERRITORY BOON for the run: the node type's
    // buff folds in alongside the permanent bonuses, and the region hands you
    // an extra starting weapon on top of your character's own.
    let bonuses = metaBonuses;
    if (runConfig && runConfig.buff) {
      bonuses = { ...metaBonuses };
      for (const [k, v] of Object.entries(runConfig.buff)) bonuses[k] = (bonuses[k] || 0) + v;
    }
    this.player = new Player(charDef, bonuses);
    if (runConfig && runConfig.weapon) this.player.addWeapon(runConfig.weapon);
    // A contract can start the run pre-cursed (Cursed Ground anomaly); the
    // Curse card stacks on top of it as normal.
    this.player.curse += (runConfig && runConfig.curse) || 0;
    this.player.x = 0; this.player.y = 0;
    this.camera.snap(0, 0);

    this.enemies.clear(); this.projectiles.clear();
    this.ebullets.clear(); this.pickups.clear(); this.particles.clear();

    this.elapsed = 0;
    this.kills = 0;
    this.gold = 0;
    this.pendingLevels = 0;
    this.boss = null;
    this.damageByWeapon = {};    // weaponId -> total damage dealt this run

    // VS-style timed run in a single chosen stage.
    this.stage = getStage(stageId || 'meadow');
    this.stageId = this.stage.id;
    this.node = runConfig;
    this.duration = (runConfig && runConfig.duration) || this.stage.duration;
    // Stage identity: later biomes hit harder and pay better. A world-map node
    // layers its own difficulty on top — rate scales at half strength so a hard
    // hex means tougher enemies more than it means a thicker crowd.
    const base = this.stage.difficulty || { hp: 1, dmg: 1, rate: 1, coin: 1 };
    const nd = runConfig && runConfig.diff ? runConfig.diff : 1;
    // THREAT rides on top of the node's own difficulty: it makes the horde
    // tankier and deadlier (and pays more) without thickening the crowd.
    this.threat = runConfig && runConfig.threat ? runConfig.threat : 0;
    const tHp = runConfig && runConfig.threatHp ? runConfig.threatHp : 1;
    const tDmg = runConfig && runConfig.threatDmg ? runConfig.threatDmg : 1;
    this.stageDiff = {
      hp: base.hp * nd * tHp,
      dmg: base.dmg * nd * tDmg,
      rate: base.rate * (1 + (nd - 1) * 0.5),
      coin: base.coin,
    };
    this.eliteMult = runConfig && runConfig.eliteMult ? runConfig.eliteMult : 1;
    // Generic anomaly contract fields (see worldmap.js ANOMALIES): a
    // multiplier on the Alpha chance, a multiplier on swarm-event size, and a
    // per-elite-kill gold gem value. All default to "off".
    this.alphaMult = (runConfig && runConfig.alphaMult) || 1;
    this.swarmMult = (runConfig && runConfig.swarmMult) || 1;
    this.eliteGoldGem = (runConfig && runConfig.eliteGoldGem) || 0;
    // The run's live horde ceiling: contract-pinned (the Gauntlet, for parity
    // on the shared seed) or the boot-time device tier. Fixed for the whole
    // run — a mid-run change would put wall-clock into the seeded sim.
    this.maxEnemies = (runConfig && runConfig.maxEnemies) || Settings.hordeCap;
    this.player.goldMult *= this.stageDiff.coin * (runConfig && runConfig.gold ? runConfig.gold : 1);
    // More XP at high threat, or a five-times-tankier horde would starve the
    // build. Threat should read as "deadlier", never as "slower".
    if (runConfig && runConfig.threatXp) this.player.xpMult *= runConfig.threatXp;
    this.won = false;
    this.reaperSpawned = false;
    this.reaper = null;
    this.reaperTimer = 0;
    // OVERRUN — the opt-in endless phase past a won finale. The win is banked
    // before the offer, so staying can only ever add.
    this.overrun = false;
    this._overrunOffered = false;
    this.overrunStart = 0;
    this.spawnTimer = 0;
    this.bossCount = 0;
    // First boss at 2:00, then every bossEvery — a Lair overrides both to turn
    // the run into a back-to-back boss siege.
    this.nextBossAt = runConfig && runConfig.firstBossAt ? runConfig.firstBossAt : 120;
    this.bossEvery = runConfig && runConfig.bossEvery ? runConfig.bossEvery : 150;
    this.swarmTimer = 45;         // periodic swarm events

    this.announce = null;        // {text, t}
    this.pendingChests = 0;      // treasure chests waiting to be opened
    this.pendingUltra = null;    // {def, isNew} — ultraweapon reveal from an Alpha
    this.multiplyPickupTimer = 42; // seconds until a golden Multiplication drops
    this.chestTimer = 55;
    // Juice / feedback state.
    this.combo = 0;
    this.bestCombo = 0;
    this.comboTimer = 0;
    this.hitstop = 0;
    this.flash = 0;
    this.flashColor = '#ffffff';
    // Level-up economy (per run).
    // Level-up agency budget — a character's `agency` (the Wanderer's
    // signature) adds to both.
    this.rerolls = 2 + ((charDef && charDef.agency) || 0);
    this.banishes = 2 + ((charDef && charDef.agency) || 0);
    this.banished = new Set();
    // Commitment-pity counters — hands since the element-mastery / weapon-
    // upgrade card was last offered. State only; the pity policy lives in the
    // content layer (upgrades.js rollUpgrades).
    this.elemPity = 0;
    this.weapPity = 0;
    this._adReviveOffered = false;  // one rewarded revive per run
    this.state = 'playing';
    this.setAnnounce(runConfig ? runConfig.nodeName : this.stage.name, 3);
  }

  addFlash(color, amount = 0.5) {
    if (Settings.reducedMotion) return;
    this.flashColor = color;
    this.flash = Math.max(this.flash, amount);
  }
  addHitstop(t) {
    if (Settings.reducedMotion) return;
    this.hitstop = Math.min(0.09, Math.max(this.hitstop, t));
  }

  setAnnounce(text, t = 2.2) { this.announce = { text, t, max: t }; }

  // ------------------------------------------------------------------- update
  update(dt) {
    if (this.state !== 'playing') return;
    dt = Math.min(dt, 1 / 30); // clamp to avoid tunneling on lag spikes

    // Hitstop: briefly freeze the simulation for punchy impacts.
    if (this.flash > 0) this.flash = Math.max(0, this.flash - dt * 3);
    if (this.hitstop > 0) {
      this.hitstop -= dt;
      this._updateParticles(dt);     // let particles keep animating during freeze
      this.camera.update(dt);
      return;
    }

    this.elapsed += dt;

    // Combo decay.
    if (this.comboTimer > 0) { this.comboTimer -= dt; if (this.comboTimer <= 0) this.combo = 0; }

    // Rebuild the enemy grid up front so aim, homing and collision all read
    // consistent current-frame positions.
    this.hash.rebuild(this.enemies.active);
    this._updatePlayer(dt);
    this._updateWeapons(dt);
    this._updateProjectiles(dt);

    this._collideProjectiles(dt);
    this._updateEnemies(dt);
    this._updateEnemyBullets(dt);
    this._updateSpawning(dt);
    this._updatePickupSpawns(dt);
    this._updatePickups(dt);
    this._updateParticles(dt);
    this.camera.follow(this.player.x, this.player.y, dt);
    // Camera shake uses its own non-sim randomness, and hitstop (above) skips
    // sim time only when reduced-motion allows it — both are accepted,
    // presentation-driven divergences from strict seeded determinism.
    this.camera.update(dt);

    if (this.announce) { this.announce.t -= dt; if (this.announce.t <= 0) this.announce = null; }
    if (this.onAchievement) this._checkLiveAchievements();

    // Cleanup dead objects.
    this.enemies.sweep();
    this.projectiles.sweep();
    this.ebullets.sweep();
    this.pickups.sweep();
    this.particles.sweep();

    // Death.
    if (this.player.hp <= 0) this._handleDeath();
  }

  _updatePlayer(dt) {
    const p = this.player;
    const [mx, my] = this.input.moveVector();
    p.vx = mx * p.moveSpeed;
    p.vy = my * p.moveSpeed;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    if (mx !== 0) p.facing = mx > 0 ? 1 : -1;
    p.bob += (Math.abs(mx) + Math.abs(my) > 0 ? dt * 10 : dt * 3);
    // Lean into horizontal movement (procedural sprite tilt).
    p.lean += (mx * 0.11 - p.lean) * Math.min(1, dt * 10);

    // Defensive: never let a bad number strand the player off the world.
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) { p.x = this.camera.x || 0; p.y = this.camera.y || 0; }

    if (p.iframes > 0) p.iframes -= dt;
    if (p.regen > 0 && p.hp < p.maxHp) p.heal(p.regen * dt);

    // Aim toward nearest enemy for directional weapons.
    const near = this.nearestEnemy(p.x, p.y, 900);
    if (near) p.aimAngle = angleOf(near.x - p.x, near.y - p.y);
    else p.aimAngle = p.facing >= 0 ? 0 : Math.PI;
  }

  _updateWeapons(dt) {
    const p = this.player;
    for (const w of p.weapons) {
      w.timer -= dt;
      if (w.timer <= 0) {
        const stats = w.def.stats(w.level);
        w.def.fire(this, stats, w.level);
        const cd = Math.max(0.05, stats.cooldown * p.cooldownMult);
        w.timer = cd;
      }
    }
  }

  // ------------------------------------------------------------- projectiles
  _updateProjectiles(dt) {
    const p = this.player;
    for (const pr of this.projectiles.active) {
      pr.life -= dt;
      if (pr.life <= 0) { this._expireProjectile(pr); continue; }
      // Cull stray traveling projectiles far from the player (safety net).
      if ((pr.kind === 'bolt' || pr.kind === 'homing') && dist2(pr.x, pr.y, p.x, p.y) > 1600 * 1600) { pr.dead = true; continue; }

      // decay per-enemy re-hit cooldowns
      if (pr.hitMap) for (const [k, v] of pr.hitMap) {
        const nv = v - dt; if (nv <= 0) pr.hitMap.delete(k); else pr.hitMap.set(k, nv);
      }

      switch (pr.kind) {
        case 'orbit': {
          pr.orbitAngle += pr.orbitSpeed * dt;
          const rr = pr.orbitRadius * p.areaMult;
          pr.x = p.x + Math.cos(pr.orbitAngle) * rr;
          pr.y = p.y + Math.sin(pr.orbitAngle) * rr;
          break;
        }
        case 'melee': {
          const prog = 1 - pr.life / pr.maxLife;
          pr.angle = pr.baseAngle + pr.sweep * prog;
          pr.x = p.x + Math.cos(pr.angle) * pr.reach * 0.5 * p.areaMult;
          pr.y = p.y + Math.sin(pr.angle) * pr.reach * 0.5 * p.areaMult;
          break;
        }
        case 'aura': {
          // Persistent damaging ring that follows the player and re-ticks.
          pr.x = p.x; pr.y = p.y;
          pr.r = pr.baseR * p.areaMult;
          break;
        }
        case 'zone': {
          // Stationary damaging pool; a gentle pulse for visual life.
          pr.r = pr.baseR * p.areaMult * (0.9 + 0.1 * Math.sin(this.elapsed * 6 + pr.spin));
          break;
        }
        case 'homing': {
          const target = this.nearestEnemy(pr.x, pr.y, 520);
          if (target) {
            const cur = angleOf(pr.vx, pr.vy);
            const desired = angleOf(target.x - pr.x, target.y - pr.y);
            const na = approachAngle(cur, desired, pr.turn * dt);
            pr.vx = Math.cos(na) * pr.speed; pr.vy = Math.sin(na) * pr.speed;
          }
          pr.angle = angleOf(pr.vx, pr.vy);   // keep the sprite/trail facing travel
          pr.x += pr.vx * dt; pr.y += pr.vy * dt;
          break;
        }
        case 'boomerang': {
          pr.spin += dt * 18;
          if (pr.life < pr.maxLife * 0.5 || pr.returning) {
            pr.returning = true;
            const [nx, ny] = norm(p.x - pr.x, p.y - pr.y);
            const sp = pr.speed * 1.15;
            pr.vx = nx * sp; pr.vy = ny * sp;
            if (dist2(pr.x, pr.y, p.x, p.y) < 26 * 26) { pr.dead = true; }
          }
          pr.x += pr.vx * dt; pr.y += pr.vy * dt;
          break;
        }
        case 'mine': {
          pr.vx *= 0.9; pr.vy *= 0.9;
          pr.x += pr.vx * dt; pr.y += pr.vy * dt;
          // Singularity mines drag nearby foes inward before detonating.
          if (pr.pull) {
            const pullR = pr.area * this.player.areaMult * 1.6;
            const near = this.hash.query(pr.x, pr.y, pullR);
            for (let i = 0; i < near.length; i++) {
              const o = near[i];
              if (o.dead || o.boss) continue;
              const dx = pr.x - o.x, dy = pr.y - o.y;
              const d = Math.sqrt(dx * dx + dy * dy) || 1;
              if (d < pullR) { const f = 260 * dt; o.x += (dx / d) * f; o.y += (dy / d) * f; }
            }
          }
          const e = this.nearestEnemy(pr.x, pr.y, pr.trigger);
          if (e || pr.life < dt) { this._explode(pr); pr.dead = true; }
          break;
        }
        case 'aoe': {
          pr.r = pr.maxR * (1 - pr.life / pr.maxLife);
          break;
        }
        default: { // bolt / nova / straight
          pr.x += pr.vx * dt; pr.y += pr.vy * dt;
          pr.spin += dt * 12;
        }
      }
    }
  }

  _expireProjectile(pr) {
    if (pr.kind === 'mine') this._explode(pr);
    pr.dead = true;
  }

  _explode(pr) {
    const area = pr.area * this.player.areaMult;
    this.spawnProjectile({
      kind: 'aoe', x: pr.x, y: pr.y, dmg: pr.dmg * 1.1, maxR: area,
      color: pr.color, life: 0.32, pierce: -1, rehit: 0,
      applyElement: pr.applyElement, weaponId: pr.weaponId,
    });
    this.particles_ring(pr.x, pr.y, area, pr.color);
    this.camera.shake(5, 0.16);
  }

  _collideProjectiles(dt) {
    for (const pr of this.projectiles.active) {
      if (pr.dead) continue;
      if (pr.kind === 'melee') { this._collideMelee(pr); continue; }
      if (pr.kind === 'mine') continue;                      // mines explode via proximity in update
      if (pr.kind === 'aura') continue;                      // aura weapon deals damage directly in fire()
      if (pr.kind === 'aoe' && pr.dmg <= 0) continue;        // cosmetic-only blasts (e.g. exploder death)
      // Own scratch buffer: _applyProjHit → dealDamage → element onHit (and
      // chain lightning) can re-query the hash, which clobbers its shared buf.
      const list = this.hash.query(pr.x, pr.y, pr.r + 40, this._collideBuf);
      for (let i = 0; i < list.length; i++) {
        const e = list[i];
        if (e.dead) continue;
        const rr = pr.r + e.r;
        if (dist2(pr.x, pr.y, e.x, e.y) > rr * rr) continue;
        if (!this._canHit(pr, e)) continue;
        this._applyProjHit(pr, e);
        if (pr.pierce >= 0) {
          pr.pierce -= 1;
          if (pr.pierce < 0) { pr.dead = true; break; }
        }
      }
    }
  }

  _collideMelee(pr) {
    const p = this.player;
    // Own scratch buffer — same nested-query hazard as _collideProjectiles.
    const list = this.hash.query(p.x, p.y, pr.reach * p.areaMult + 40, this._meleeBuf);
    const reach = pr.reach * p.areaMult;
    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      if (e.dead) continue;
      const d = dist(p.x, p.y, e.x, e.y);
      if (d > reach + e.r) continue;
      const a = angleOf(e.x - p.x, e.y - p.y);
      let diff = Math.abs(((a - pr.angle + Math.PI * 3) % TAU) - Math.PI);
      if (diff > pr.arcHalf) continue;
      if (!this._canHit(pr, e)) continue;
      this._applyProjHit(pr, e);
    }
  }

  _canHit(pr, e) {
    if (pr.rehit > 0) {
      if (pr.hitMap && pr.hitMap.has(e.id)) return false;
      if (!pr.hitMap) pr.hitMap = new Map();
      pr.hitMap.set(e.id, pr.rehit);
      return true;
    }
    if (!pr.hitIds) pr.hitIds = new Set();
    if (pr.hitIds.has(e.id)) return false;
    pr.hitIds.add(e.id);
    return true;
  }

  _applyProjHit(pr, e) {
    const p = this.player;
    let dmg = pr.dmg;
    const crit = this.rng.next() < p.critChance;
    if (crit) dmg *= p.critMult;
    // Knockback direction: away from the projectile; if the projectile is
    // exactly on the enemy, fall back to away-from-player — as a whole vector,
    // never per-component (mixing components of two vectors aims sideways).
    let kdx = e.x - pr.x, kdy = e.y - pr.y;
    if (kdx === 0 && kdy === 0) { kdx = e.x - p.x; kdy = e.y - p.y; }
    const [kx, ky] = norm(kdx, kdy);
    this.dealDamage(e, dmg, {
      crit, knockback: pr.knockback, kx, ky,
      applyElement: pr.applyElement !== false, sourceEl: p.element,
      weaponId: pr.weaponId,
    });
    // Evolved-weapon chain lightning (independent of element).
    if (pr.chain > 0) {
      const near = this.enemiesInRadius(e.x, e.y, 160);
      let hit = 0;
      for (const o of near) {
        if (o === e || o.dead) continue;
        this.dealDamage(o, dmg * 0.5, { applyElement: false, weaponId: pr.weaponId });
        this.particles_spark(o.x, o.y, pr.color, 3);
        if (++hit >= pr.chain) break;
      }
    }
    this.particles_spark(pr.x, pr.y, pr.color, crit ? 6 : 3);
    Audio.hit();
  }

  // Public damage entry point (also used by elements/AoE).
  dealDamage(e, dmg, opts = {}) {
    if (e.dead) return;
    if (opts.weaponId) this.damageByWeapon[opts.weaponId] = (this.damageByWeapon[opts.weaponId] || 0) + dmg;
    e.hp -= dmg;
    e.hitFlash = 0.09;
    // Squash punch — sprite visibly recoils from the hit (decays in update).
    e.squash = Math.min(0.45, (e.squash || 0) + (opts.crit ? 0.32 : 0.2));
    if (opts.knockback && !e.boss) {
      const kb = opts.knockback * (opts.crit ? 1.5 : 1);
      e.vx += (opts.kx || 0) * kb;
      e.vy += (opts.ky || 0) * kb;
    }
    this._damageNumber(e.x, e.y - e.r, Math.round(dmg), opts.crit, e);
    if (opts.applyElement) {
      const el = getElement(this.player.element);
      if (el && el.onHit) el.onHit(this, e, dmg, this.player);
      // Character signature (content-authored, engine-blind). Gated behind
      // applyElement exactly like the element hook: signature riders damage
      // with applyElement:false, so neither can recurse into the other.
      const ch = this.player.char;
      if (ch && ch.hooks && ch.hooks.onHit) ch.hooks.onHit(this, e, dmg, !!opts.crit);
    }
    if (e.hp <= 0) this._killEnemy(e);
  }

  // ------------------------------------------------------------------ enemies
  _updateEnemies(dt) {
    const p = this.player;
    const enemies = this.enemies.active;
    for (const e of enemies) {
      if (e.dead) continue;
      if (e.hitFlash > 0) e.hitFlash -= dt;
      if (e.squash > 0) e.squash = Math.max(0, e.squash - dt * 3.2);
      if (e.spawnT > 0) e.spawnT -= dt;

      // Status effects.
      let speedMult = 1;
      if (e.freezeT > 0) { e.freezeT -= dt; speedMult = 0; }
      if (e.slowT > 0) { e.slowT -= dt; speedMult *= e.slowMult; }
      if (e.burnT > 0) { e.burnT -= dt; e._dot(this, e.burnDps * dt); }
      if (e.poisonT > 0) { e.poisonT -= dt; e._dot(this, e.poisonDps * e.poisonStacks * dt); }
      if (e.dead) continue;

      // Behavior.
      this._enemyAI(e, dt, speedMult);

      // Apply knockback velocity + friction.
      e.x += e.vx * dt; e.y += e.vy * dt;
      e.vx *= 0.86; e.vy *= 0.86;
      e.bob += dt * 8;

      // Contact damage to player.
      const rr = e.r + p.r * 0.7;
      if (!e.noContact && dist2(e.x, e.y, p.x, p.y) < rr * rr) {
        if (p.iframes <= 0) {
          const dmg = e.dmg * this.dmgScale();
          Audio.hurt(); Platform.haptic('light');
          p.takeDamage(dmg);
          this.camera.shake(6, 0.2);
          this.addFlash('#ff2d55', 0.28);
          this.particles_spark(p.x, p.y, '#ff4d6d', 8);
          // Character signature contact hook (Rimeward: the attacker pays).
          const ch = p.char;
          if (ch && ch.hooks && ch.hooks.onContact) ch.hooks.onContact(this, e);
          // small knockback to enemy so it doesn't perma-stick
          const [nx, ny] = norm(e.x - p.x, e.y - p.y);
          e.vx += nx * 120; e.vy += ny * 120;
        }
      }
    }
    this._separate(enemies, dt);
  }

  _enemyAI(e, dt, speedMult) {
    const p = this.player;
    const sp = e.speed * speedMult * this.speedScale();
    const toA = angleOf(p.x - e.x, p.y - e.y);
    switch (e.behavior) {
      case 'swarm': {
        const wob = Math.sin(this.elapsed * 3 + e.seed) * 0.5;
        const a = toA + wob;
        e.x += Math.cos(a) * sp * dt; e.y += Math.sin(a) * sp * dt;
        break;
      }
      case 'charger': {
        e.chargeT = (e.chargeT || 0) - dt;
        if (e.dashing) {
          e.dashT -= dt;
          e.x += Math.cos(e.dashA) * sp * 3.4 * dt;
          e.y += Math.sin(e.dashA) * sp * 3.4 * dt;
          if (e.dashT <= 0) { e.dashing = false; e.chargeT = 1.6; }
        } else if (e.chargeT <= 0 && dist2(e.x, e.y, p.x, p.y) < 460 * 460) {
          e.dashing = true; e.dashT = 0.45; e.dashA = toA;
          this.particles_spark(e.x, e.y, '#ff4d6d', 4);
        } else {
          e.x += Math.cos(toA) * sp * 0.7 * dt; e.y += Math.sin(toA) * sp * 0.7 * dt;
        }
        break;
      }
      case 'shooter': {
        const d = dist(e.x, e.y, p.x, p.y);
        const want = 300;
        let mv = 0;
        if (d > want + 40) mv = 1; else if (d < want - 40) mv = -0.8;
        e.x += Math.cos(toA) * sp * mv * dt; e.y += Math.sin(toA) * sp * mv * dt;
        e.shootT = (e.shootT || this.rng.range(0.5, 1.5)) - dt;
        if (e.shootT <= 0 && d < 640) {
          e.shootT = e.shootCd || 2.1;
          const bs = 190;
          this.spawnEnemyBullet(e.x, e.y, Math.cos(toA) * bs, Math.sin(toA) * bs, e.dmg * 0.8, e.tint);
        }
        break;
      }
      case 'brute': {
        e.x += Math.cos(toA) * sp * dt; e.y += Math.sin(toA) * sp * dt;
        break;
      }
      case 'orbiter': {
        // Circles the player at a preferred radius while slowly closing in.
        const d = dist(e.x, e.y, p.x, p.y);
        const want = e.orbitPref || 220;
        const radial = d > want + 30 ? 1 : d < want - 30 ? -0.7 : 0;
        const tan = toA + Math.PI / 2;
        e.x += (Math.cos(toA) * radial + Math.cos(tan) * 0.9) * sp * dt;
        e.y += (Math.sin(toA) * radial + Math.sin(tan) * 0.9) * sp * dt;
        break;
      }
      case 'phantom': {
        // Fades and blinks toward the player periodically.
        e.blinkT = (e.blinkT ?? this.rng.range(1.6, 3)) - dt;
        if (e.blinkT <= 0) {
          e.blinkT = this.rng.range(2, 3.4);
          const ba = this.rng.angle(), bd = this.rng.range(70, 170);
          this.particles_burst(e.x, e.y, e.tint, 8);
          e.x = p.x + Math.cos(ba) * bd; e.y = p.y + Math.sin(ba) * bd;
          this.particles_burst(e.x, e.y, e.tint, 8);
        } else {
          e.x += Math.cos(toA) * sp * 0.55 * dt; e.y += Math.sin(toA) * sp * 0.55 * dt;
        }
        break;
      }
      default: { // 'chase'
        e.x += Math.cos(toA) * sp * dt; e.y += Math.sin(toA) * sp * dt;
      }
    }

    // Boss custom update hook.
    if (e.boss && e.bossUpdate) e.bossUpdate(this, e, dt);

    // Teleport very-far enemies back into range (keeps swarm engaged & endless).
    if (dist2(e.x, e.y, p.x, p.y) > CONFIG.spawn.despawnRadius * CONFIG.spawn.despawnRadius && !e.boss) {
      const a = this.rng.angle();
      e.x = p.x + Math.cos(a) * CONFIG.spawn.spawnRadius;
      e.y = p.y + Math.sin(a) * CONFIG.spawn.spawnRadius;
    }
  }

  // Light separation so the swarm doesn't collapse to a single point.
  _separate(enemies, dt) {
    for (const e of enemies) {
      if (e.dead || e.boss) continue;
      const near = this.hash.query(e.x, e.y, e.r * 2);
      let px = 0, py = 0, n = 0;
      for (let i = 0; i < near.length && n < 5; i++) {
        const o = near[i];
        if (o === e || o.dead) continue;
        const dx = e.x - o.x, dy = e.y - o.y;
        const d2 = dx * dx + dy * dy;
        const min = (e.r + o.r) * 0.8;
        if (d2 > 0 && d2 < min * min) {
          const d = Math.sqrt(d2);
          px += dx / d; py += dy / d; n++;
        }
      }
      if (n > 0) { e.x += px * 26 * dt; e.y += py * 26 * dt; }
    }
  }

  _killEnemy(e) {
    if (e.dead) return;
    e.dead = true;
    this.kills++;
    if (this.kills === 1) this.unlockAch('first_blood');
    this.combo++;
    this.comboTimer = 3;
    if (this.combo > this.bestCombo) this.bestCombo = this.combo;
    this.particles_burst(e.x, e.y, e.tint, e.boss ? 30 : 8);
    this._deathPop(e);
    // Bestiary discovery — keyed off defId so the engine names no content;
    // the app layer owns dedupe and persistence (same seam as onAchievement).
    if (this.onDiscover && e.defId) this.onDiscover((e.boss ? 'b:' : 'e:') + e.defId);
    // Character signature kill hook (Cinderheart, Aegis, …).
    {
      const ch = this.player.char;
      if (ch && ch.hooks && ch.hooks.onKill) ch.hooks.onKill(this, e);
    }
    if (e.elite && !e.boss) this.addHitstop(0.04);
    if (e.boss) {
      this.camera.shake(16, 0.6);
      this.addHitstop(0.09);
      this.addFlash('#ffffff', 0.6);
      Audio.death();
      Platform.haptic('heavy');
      // The achievement a boss grants is authored on the def, not matched on
      // its id here — the engine must not name content.
      this.unlockAch(e.killAch || 'boss_slayer');
      // ALPHA trophy: an ultraweapon — a new one if possible, else empower an owned one.
      if (e.super) {
        const ultras = [...Weapons.values()].filter((d) => d.ultra);
        const unowned = ultras.filter((d) => !this.player.hasWeapon(d.id));
        const pool = unowned.length ? unowned : ultras;
        if (pool.length) {
          const def = this.rng.pick(pool);
          const isNew = !this.player.hasWeapon(def.id);
          this.player.addWeapon(def.id);          // adds, or levels an owned ultra
          this.pendingUltra = { def, isNew };     // reveal screen opens from main
          this.unlockAch('superboss');
        }
        this.spawnGem(e.x + 30, e.y, 'heart', 1);
        this.spawnGem(e.x - 30, e.y, 'heart', 1);
      }
      // Another boss can still be alive (the Reaper arrives on schedule
      // regardless), so hand the bar over instead of blanking it.
      this.boss = this.enemies.active.find((o) => o.boss && !o.dead && o !== e) || null;
      this._dropChest(e.x, e.y);
      for (let i = 0; i < 14; i++) this.spawnGem(e.x + this.rng.range(-60, 60), e.y + this.rng.range(-60, 60), 'xp', e.xp / 14);
      if (e.gold) this.gold += Math.round(e.gold * this.player.goldMult * this.goldScale());
    } else {
      this.spawnGem(e.x, e.y, 'xp', e.xp);
      // Golden Horde anomaly: elites bleed a gold gem on top of their normal
      // drops. A pickup, not a direct payment — it flows through goldMult on
      // collect like any other gold. (The dormant per-enemy `e.gold` field is
      // deliberately left untouched.)
      if (e.elite && this.eliteGoldGem) this.spawnGem(e.x, e.y, 'gold', this.eliteGoldGem);
      const luck = this.player.luck;
      // Higher combo → slightly better gold drops (rewards aggression, capped).
      const comboBonus = Math.min(0.05, this.combo * 0.0005);
      // Gem worth rises with run time, but slowly and with a ceiling: a longer
      // run already yields far more kills, and letting per-kill value climb on
      // top of that made late/long hexes pay orders of magnitude more than
      // early ones. Growth in the count, not in both.
      if (this.rng.next() < 0.03 + luck * 0.02 + comboBonus) {
        this.spawnGem(e.x, e.y, 'gold', 1 + Math.min(3, Math.floor(this.elapsed / 150)));
      }
      if (this.rng.next() < 0.006) this.spawnGem(e.x, e.y, 'heart', 1);
      if (this.rng.next() < 0.004) this.spawnGem(e.x, e.y, 'magnet', 1);
      if (this.rng.next() < 0.0016 + luck * 0.001) this.spawnGem(e.x, e.y, 'bomb', 1);
      Audio.death();
    }
    // onDeath behaviors.
    if (e.splitInto && e.splitCount) {
      for (let i = 0; i < e.splitCount; i++) {
        const a = this.rng.angle();
        const ne = this.spawnEnemy(e.splitInto, e.x + Math.cos(a) * 14, e.y + Math.sin(a) * 14);
        if (ne) { ne.hp *= 0.7; ne.maxHp *= 0.7; }
      }
    }
    if (e.explodeOnDeath) {
      const col = e.explodeColor || '#ff8a3d';
      this.spawnProjectile({
        kind: 'aoe', x: e.x, y: e.y, dmg: 0, maxR: 70, color: col,
        life: 0.3, pierce: -1, rehit: 0, applyElement: false,
      });
      // The blast visual is decorative; this is the actual player hitbox.
      if (dist2(e.x, e.y, this.player.x, this.player.y) < 70 * 70 && this.player.iframes <= 0) {
        this.player.takeDamage(e.dmg * 1.4); Audio.hurt(); Platform.haptic('light'); this.camera.shake(8, 0.25); this.addFlash('#ff2d55', 0.3);
      }
      this.particles_ring(e.x, e.y, 70, col);
    }
    // Shatter into a radial burst of shards (e.g. crystal golems).
    if (e.shardBurst > 0) {
      const n = e.shardBurst;
      const off = this.rng.angle();
      for (let i = 0; i < n; i++) {
        const a = off + (i / n) * TAU;
        this.spawnEnemyBullet(e.x, e.y, Math.cos(a) * 150, Math.sin(a) * 150, e.dmg * 0.5, e.tint);
      }
      this.particles_ring(e.x, e.y, 60, e.tint);
    }
  }

  // ------------------------------------------------------------- enemy bullets
  spawnEnemyBullet(x, y, vx, vy, dmg, color) {
    const b = this.ebullets.spawn();
    b.x = x; b.y = y; b.vx = vx; b.vy = vy; b.r = 7; b.dmg = dmg;
    b.color = color || '#ff5cf0'; b.life = 5;
    return b;
  }
  _updateEnemyBullets(dt) {
    const p = this.player;
    for (const b of this.ebullets.active) {
      b.life -= dt;
      b.x += b.vx * dt; b.y += b.vy * dt;
      if (b.life <= 0) { b.dead = true; continue; }
      const rr = b.r + p.r * 0.7;
      if (dist2(b.x, b.y, p.x, p.y) < rr * rr) {
        if (p.iframes <= 0) {
          p.takeDamage(b.dmg * this.dmgScale());
          Audio.hurt(); Platform.haptic('light'); this.camera.shake(5, 0.18);
          this.addFlash('#ff2d55', 0.24);
          this.particles_spark(p.x, p.y, '#ff4d6d', 6);
        }
        b.dead = true;
      }
    }
  }

  // ----------------------------------------------------------------- spawning
  // Time-based: enemies stream in continuously, escalating by the minute.
  // Bosses arrive on a timer (dropping treasure chests), periodic swarm events
  // add pressure, and at the end of the run the Reaper appears.
  _updateSpawning(dt) {
    const sp = CONFIG.spawn;
    const t = this.elapsed;

    // End of run → the region's finale boss.
    if (!this.reaperSpawned && t >= this.duration) {
      this.reaperSpawned = true;
      this.won = true;               // surviving to the finale counts as a clear
      this.reaper = this._spawnBoss(this.stage.finaleBoss, { finale: true });
      // The arrival shout is authored on the boss def (`cry`), not written here
      // — four regions have four finales, and the engine must not name any of
      // them. The fallback keeps a def that omits it readable.
      this.setAnnounce((this.reaper && this.reaper.cry) || '⚰  THE END COMES  ⚰', 4);
      this.addFlash('#ffffff', 0.7);
      this.camera.shake(18, 0.8);
    }
    if (this.reaperSpawned && !this.overrun) {
      // Grace period, then the run is won regardless (you outlasted the stage).
      // Keyed to the Reaper itself, not to "any boss bar is empty" — a scheduled
      // boss still alive when the Reaper lands used to end the run the instant
      // it died, skipping the Reaper entirely.
      this.reaperTimer += dt;
      if (this.reaperTimer > 25 || !this.reaper || this.reaper.dead) {
        // The finale is resolved and the win is already banked. Node runs may
        // elect to stay for OVERRUN; the Gauntlet's fixed contract may not.
        // No callback wired (headless drives, legacy) → the classic instant win.
        if (this.onOfferOverrun && !this._overrunOffered && !(this.node && this.node.gauntlet)) {
          this._overrunOffered = true;
          this.state = 'overrunoffer';   // sim frozen while the choice is up
          this.onOfferOverrun();
          return;
        }
        this._win();
      }
    }

    // Continuous spawning at a gentle, time-scaling rate (sparse early).
    const curseCount = 1 + this.player.curse * 0.25;
    if (this.enemies.count < this.maxEnemies) {
      this.spawnTimer -= dt;
      if (this.spawnTimer <= 0) {
        this.spawnTimer = sp.pulse;
        const rate = Math.min(sp.rateMax, sp.rateStart + t * sp.ratePerSec) * curseCount * this.stageDiff.rate;
        const perPulse = Math.max(1, Math.round(rate * sp.pulse));
        this._spawnBatch(perPulse, (0.04 + t * 0.00003) * this.eliteMult, 1 + Math.floor(t / 360));
      }
    }

    // Occasional swarm event: a modest ring of a single foe (a "minute" swarm).
    this.swarmTimer -= dt;
    if (this.swarmTimer <= 0) {
      this.swarmTimer = this.rng.range(45, 70);
      this._swarmEvent();
    }

    // Boss events on a timer — each drops a treasure chest on death. Past the
    // 4-minute mark there's a chance the slot is taken by an ALPHA super boss,
    // a far tougher variant that yields an ultraweapon.
    if (!this.boss && (!this.reaperSpawned || this.overrun) && t >= this.nextBossAt) {
      this.nextBossAt += this.bossEvery;
      const id = this.stage.bosses[this.bossCount % this.stage.bosses.length];
      // Alphas belong to the back half of a run. On a short hex that has to be
      // relative, or a 4-minute Hoard could never drop an ultraweapon at all.
      const alphaAfter = Math.min(240, this.duration * 0.5);
      const alpha = t >= alphaAfter && this.rng.next() < (0.16 + this.player.curse * 0.03) * this.alphaMult;
      this._spawnBoss(id, { alpha });
    }
  }

  _swarmEvent() {
    const p = this.player;
    const id = this.rng.weighted(this.stage.enemies, (eid) => this._spawnWeight(eid));
    const n = Math.round((8 + this.elapsed / 22) * (1 + this.player.curse * 0.25) * this.swarmMult);
    const baseA = this.rng.angle();
    const arc = this.rng.range(0.6, 1.4);
    for (let i = 0; i < n && this.enemies.count < this.maxEnemies; i++) {
      const a = baseA + (this.rng.next() - 0.5) * arc;
      const d = CONFIG.spawn.spawnRadius + this.rng.range(-40, 120);
      this.spawnEnemy(id, p.x + Math.cos(a) * d, p.y + Math.sin(a) * d);
    }
  }

  _win() {
    if (this.state !== 'playing') return;
    this.won = true;
    this.state = 'dead';
    Audio.chest();
    const summary = this._summary(true);
    if (this.onGameOver) this.onGameOver(summary);
  }

  // ------------------------------------------------------------------ overrun
  // Minutes survived past the finale — drives the escalating scaling below.
  overrunMinutes() { return this.overrun ? (this.elapsed - this.overrunStart) / 60 : 0; }
  // Take the offer: the sim resumes, boss cadence restarts on the overrun
  // clock, and every scale below starts climbing. Quadratic in minutes for the
  // same reason Threat tiers are quadratic in tiers: the phase must outrun any
  // build, or "endless" becomes "forever".
  beginOverrun() {
    if (this.state !== 'overrunoffer') return;
    this.overrun = true;
    this.overrunStart = this.elapsed;
    this.reaperTimer = 0;
    this.nextBossAt = this.elapsed + 45;
    this.state = 'playing';
    this.setAnnounce('🌀 OVERRUN — THE HORDE DOES NOT RELENT', 4);
    this.addFlash('#b06bff', 0.5);
    Audio.boss();
  }
  // Decline the offer: the run ends as the win it already is.
  bankWin() {
    if (this.state !== 'overrunoffer') return;
    this.state = 'playing';
    this._win();
  }

  // Force a boss now (debug / testing).
  forceBoss() {
    if (this.boss) return;
    const id = this.stage.bosses[this.bossCount % this.stage.bosses.length];
    this._spawnBoss(id);
  }

  _updatePickupSpawns(dt) {
    // Golden Multiplication pickup (rare, thematic).
    this.multiplyPickupTimer -= dt;
    if (this.multiplyPickupTimer <= 0) {
      this.multiplyPickupTimer = this.rng.range(55, 85);
      const a = this.rng.angle(), d = this.rng.range(200, 380);
      this.spawnGem(this.player.x + Math.cos(a) * d, this.player.y + Math.sin(a) * d, 'multiply', 1);
    }
    this.chestTimer -= dt;
    if (this.chestTimer <= 0) {
      this.chestTimer = this.rng.range(60, 90);
      const a = this.rng.angle(), d = this.rng.range(220, 380);
      this.spawnGem(this.player.x + Math.cos(a) * d, this.player.y + Math.sin(a) * d, 'chest', 1);
    }
  }

  // Spawn weight: fodder dominates early; tough (tier 3+) foes ramp in over time.
  _spawnWeight(id) {
    const def = getEnemy(id);
    if (!def) return 1;
    const tier = def.tier || 1;
    const base = def.weight ?? Math.max(1, 6 - tier * 1.5);
    if (tier >= 3) return base * Math.min(1.8, 0.3 + this.elapsed / 150);
    return base;
  }

  // Spawn a batch of `count` enemies in a ring around the player, from the
  // stage's weighted roster, plus a chance of one or more elites.
  _spawnBatch(count, eliteChance = 0, eliteCount = 1) {
    const stage = this.stage;
    const p = this.player;
    const room = this.maxEnemies - this.enemies.count;
    count = Math.max(0, Math.min(count, room));
    for (let i = 0; i < count; i++) {
      const a = this.rng.angle();
      const d = CONFIG.spawn.spawnRadius + this.rng.range(-80, 140);
      const id = this.rng.weighted(stage.enemies, (eid) => this._spawnWeight(eid));
      this.spawnEnemy(id, p.x + Math.cos(a) * d, p.y + Math.sin(a) * d);
    }
    if (this.rng.next() < eliteChance) {
      for (let k = 0; k < eliteCount; k++) {
        const a = this.rng.angle();
        const id = this.rng.pick(stage.elites);
        const e = this.spawnEnemy(id, p.x + Math.cos(a) * CONFIG.spawn.spawnRadius, p.y + Math.sin(a) * CONFIG.spawn.spawnRadius);
        if (e) this._makeElite(e);
      }
    }
  }

  _makeElite(e) {
    e.elite = true;
    e.hp *= 4; e.maxHp *= 4; e.r *= 1.4; e.dmg *= 1.4; e.xp *= 5; e.gold = (e.gold || 0) + 3;
    e.tintGlow = '#ffd15c';
  }

  spawnEnemy(id, x, y, opts = {}) {
    const def = getEnemy(id);
    if (!def) return null;
    const e = this.enemies.spawn();
    const hpScale = this.hpScale();
    e.id = this._enemyId++;
    e.defId = id;
    e.x = x; e.y = y; e.vx = 0; e.vy = 0;
    e.r = def.r || 13;
    e.maxHp = def.hp * hpScale;
    e.hp = e.maxHp;
    e.speed = def.speed;
    e.dmg = def.dmg;
    e.xp = def.xp;
    e.gold = def.gold || 0;
    e.behavior = def.behavior || 'chase';
    e.sprite = def.sprite;
    e.tint = def.tint || '#ffffff';
    e.tintGlow = null;
    e.seed = this.rng.range(0, 100);
    e.boss = false; e.elite = false;
    e.noContact = false;
    e.hitFlash = 0;
    e.squash = 0;
    e._numPt = null;              // damage-number aggregation ref (recycled object)
    e.spawnT = 0.3;               // scale-in on spawn
    e.spawnMax = 0.3;
    e.bob = this.rng.range(0, 6);
    e.spriteScale = def.spriteScale || 1;
    // status
    e.burnT = 0; e.burnDps = 0; e.poisonT = 0; e.poisonDps = 0; e.poisonStacks = 0;
    e.slowT = 0; e.slowMult = 1; e.freezeT = 0;
    // death behaviors
    e.splitInto = def.splitInto || null;
    e.splitCount = def.splitCount || 0;
    e.explodeOnDeath = !!def.explodeOnDeath;
    e.explodeColor = def.explodeColor || '#ff8a3d';
    e.shardBurst = def.shardBurst || 0;
    e.shootCd = def.shootCd;
    // behavior-specific
    e.orbitPref = def.orbitPref || 0;
    e.ghost = !!def.ghost;
    e.blinkT = null;
    e.dashing = false; e.chargeT = this.rng.range(0.4, 1.6);
    e._dot = enemyDot;
    return e;
  }

  _spawnBoss(bossId, opts = {}) {
    const def = getBoss(bossId || this.stage.bosses[0]);
    if (!def) return null;
    const p = this.player;
    const a = this.rng.angle();
    const e = this.enemies.spawn();
    e.id = this._enemyId++;
    e.defId = def.id; e.boss = true; e.elite = false;
    e.x = p.x + Math.cos(a) * 520; e.y = p.y + Math.sin(a) * 520;
    e.vx = 0; e.vy = 0;
    e.r = def.r || 48;
    // Each successive boss is tougher; the Reaper is on another level.
    const scale = this.hpScale() * (1 + this.bossCount * 0.5);
    e.maxHp = def.hp * scale; e.hp = e.maxHp;
    e.speed = def.speed; e.dmg = def.dmg; e.xp = def.xp; e.gold = def.gold || 20;
    e.behavior = def.behavior || 'brute';
    e.sprite = def.sprite; e.tint = def.tint || '#ff5cf0'; e.tintGlow = '#ff2d9b';
    e.name = def.name; e.seed = this.rng.range(0, 100);
    e.spriteScale = def.spriteScale || 1;
    e.hitFlash = 0; e.bob = 0;
    e.squash = 0; e.spawnT = 0.5; e.spawnMax = 0.5;
    // Optional def-authored fields, read from the same keys spawnEnemy uses so
    // a boss can opt into them deliberately. resetEnemy() has already zeroed
    // everything else, so nothing leaks in from the recycled corpse.
    e.shardBurst = def.shardBurst || 0;
    e.shootCd = def.shootCd || 0;
    e.ghost = !!def.ghost;
    e.orbitPref = def.orbitPref || 0;
    e.explodeOnDeath = !!def.explodeOnDeath;
    e.explodeColor = def.explodeColor || '#ff8a3d';
    e.chargeT = 2;
    e._dot = enemyDot;
    e.bossState = {};
    e.bossUpdate = def.update || null;
    e.killAch = def.killAch || null;
    e.cry = def.cry || null;        // finale arrival shout, authored on the def
    // The end-of-run finale boss (the stage's `finaleBoss`). A flag, not an id
    // comparison — the engine must not name content, and a content rename must
    // not be able to break the win condition.
    e.finale = !!opts.finale;
    // ALPHA super boss: massively stronger, glows gold, drops an ultraweapon.
    e.super = !!opts.alpha;
    if (e.super) {
      e.maxHp *= 4.5; e.hp = e.maxHp;
      e.dmg *= 1.5; e.r *= 1.25; e.spriteScale *= 1.15;
      e.xp *= 3; e.gold = (e.gold || 20) * 4;
      e.tintGlow = '#ffd15c';
      e.name = 'ALPHA ' + def.name;
    }
    if (def.onSpawn) def.onSpawn(this, e);
    this.boss = e;
    if (!e.finale) this.bossCount++;
    Audio.boss();
    this.camera.shake(e.super ? 15 : 10, 0.5);
    this.addFlash(e.super ? '#ffd15c' : '#ff2d9b', 0.5);
    this.setAnnounce(e.super ? '☠ ' + e.name + ' ☠' : '⚠ ' + def.name + ' ⚠', 3);
    return e;
  }

  _dropChest(x, y) { this.spawnGem(x, y, 'chest', 1); }

  // -------------------------------------------------------------------- pickups
  spawnGem(x, y, kind, value) {
    // Late-run crowd control: once the field is dense, fold new xp into a
    // nearby gem instead of spawning another one (VS-style gem merging).
    if (kind === 'xp' && this.pickups.count > 320) {
      let best = null, bestD = 56 * 56;
      for (const o of this.pickups.active) {
        if (o.dead || o.kind !== 'xp') continue;
        const d = dist2(o.x, o.y, x, y);
        if (d < bestD) { best = o; bestD = d; }
      }
      if (best) { best.value += value; return best; }
    }
    const g = this.pickups.spawn();
    g.x = x; g.y = y; g.kind = kind; g.value = value;
    g.vx = this.rng.range(-40, 40); g.vy = this.rng.range(-60, -20);
    g.magnetized = false; g.bob = this.rng.range(0, 6);
    g.r = kind === 'xp' ? 8 : 12;
    g.age = 0;
    return g;
  }

  _updatePickups(dt) {
    const p = this.player;
    const pr = p.pickupRadius * p.magnetMult;
    for (const g of this.pickups.active) {
      g.age += dt;
      // spray settle
      g.x += g.vx * dt; g.y += g.vy * dt;
      g.vx *= 0.9; g.vy *= 0.9;
      const d2 = dist2(g.x, g.y, p.x, p.y);
      // All drop kinds attract within the pickup radius; chests need a walk-over.
      const collectR = g.kind === 'chest' ? pr * 0.55 : pr;
      if (g.magnetized || d2 < collectR * collectR) {
        g.magnetized = true;
        const [nx, ny] = norm(p.x - g.x, p.y - g.y);
        const speed = 380 + (1 - clamp(Math.sqrt(d2) / 300, 0, 1)) * 320;
        g.x += nx * speed * dt; g.y += ny * speed * dt;
      }
      const pickR = p.r + g.r;
      if (d2 < pickR * pickR) { this._collect(g); g.dead = true; }
    }
  }

  _collect(g) {
    const p = this.player;
    switch (g.kind) {
      case 'xp': {
        const lv = p.gainXp(g.value);
        if (lv > 0) { this.pendingLevels += lv; Audio.pickup(); }
        else Audio.pickup();
        break;
      }
      case 'gold':
        this.gold += Math.round(g.value * p.goldMult * this.goldScale()); Audio.gold();
        this.particles_spark(g.x, g.y, '#ffd15c', 3);
        break;
      case 'heart': p.heal(p.maxHp * 0.25); Audio.pickup(); this.particles_burst(p.x, p.y, '#ff2d55', 10); break;
      case 'magnet':
        // Vacuum EVERY drop on the field (xp, gold, hearts…), not just gems.
        for (const o of this.pickups.active) if (o.kind !== 'chest') o.magnetized = true;
        Audio.pickup(); this.setAnnounce('✦ MAGNET ✦', 1.4); break;
      case 'bomb': this._screenNuke(); break;
      case 'multiply': {
        // Stacks the multiplier only — bumping the `multiply` passive here
        // spent one of the five Multiplication CARDS per pickup, so collecting
        // a power-up cost you a bigger one later and the optimal play was to
        // leave it lying on the ground. Never again.
        // The factor tapers with the number already collected (CONFIG.multiply);
        // announce what was ACTUALLY applied, never a fixed ×1.3.
        const factor = p.gainMultiply();
        Audio.multiply(); this.camera.shake(8, 0.3);
        this.particles_burst(p.x, p.y, '#ffd15c', 26);
        this.setAnnounce('✦ MULTIPLICATION ×' + factor.toFixed(2) + ' ✦', 2.6);
        break;
      }
      case 'chest': {
        Audio.chest();
        this.gold += Math.round((10 + Math.min(50, this.elapsed / 10)) * p.goldMult);
        this.pendingChests = (this.pendingChests || 0) + 1;  // opened via the chest screen
        this.particles_burst(g.x, g.y, '#ffd15c', 24);
        this.camera.shake(6, 0.25);
        break;
      }
    }
  }

  _screenNuke() {
    Audio.boss();
    this.camera.shake(14, 0.5);
    for (const e of this.enemies.active) {
      if (e.dead || e.boss) continue;
      this.dealDamage(e, 9999, { applyElement: false });
    }
    for (const b of this.ebullets.active) b.dead = true;
    this.particles_ring(this.player.x, this.player.y, 700, '#7df9ff');
  }

  // ------------------------------------------------------------- projectile API
  spawnProjectile(o) {
    const pr = this.projectiles.spawn();
    pr.kind = o.kind || 'bolt';
    pr.x = o.x; pr.y = o.y;
    pr.vx = o.vx || 0; pr.vy = o.vy || 0;
    pr.r = o.r || 8;
    pr.dmg = o.dmg || 1;
    pr.pierce = o.pierce ?? 0;
    pr.rehit = o.rehit || 0;
    pr.life = o.life || 1;
    pr.maxLife = pr.life;
    pr.color = o.color || '#7df9ff';
    pr.knockback = o.knockback || 0;
    pr.weaponId = o.weaponId || '';
    pr.applyElement = o.applyElement !== false;
    pr.spin = 0;
    pr.area = o.area || 0;
    pr.trigger = o.trigger || 40;
    pr.hitIds = null; pr.hitMap = null;
    pr.drawScale = o.drawScale || 1;
    pr.returning = false;
    pr.chain = o.chain || 0;      // evolved chain-lightning bounces
    pr.pull = o.pull || false;    // singularity gravity pull
    // kind-specific
    if (pr.kind === 'orbit') {
      pr.orbitAngle = o.orbitAngle || 0;
      pr.orbitRadius = o.orbitRadius || 70;
      pr.orbitSpeed = o.orbitSpeed || 3;
    }
    if (pr.kind === 'melee') {
      pr.baseAngle = o.baseAngle || 0;
      pr.angle = pr.baseAngle;
      pr.arcHalf = o.arcHalf || 0.9;
      pr.sweep = o.sweep || 1.6;
      pr.reach = o.reach || 90;
    }
    if (pr.kind === 'homing') { pr.speed = o.speed || 260; pr.turn = o.turn || 6; }
    if (pr.kind === 'boomerang') { pr.speed = o.speed || 300; }
    if (pr.kind === 'aoe') { pr.maxR = o.maxR || 60; pr.r = 4; }
    if (pr.kind === 'mine') { pr.area = o.area || 80; }
    if (pr.kind === 'aura' || pr.kind === 'zone') { pr.baseR = o.baseR || o.r || 90; pr.spin = o.spin || 0; }
    pr.angle = pr.angle || angleOf(pr.vx, pr.vy);
    return pr;
  }

  // MULTIPLICATION — effective projectile count & any overflow damage.
  // projMult multiplies, projAdd (Duplicate) adds flat projectiles.
  mult(base) {
    const p = this.player;
    const raw = base * p.projMult + p.projAdd;
    const cap = base + 12;
    const count = Math.max(1, Math.round(Math.min(raw, cap)));
    const dmgMult = raw > cap ? raw / cap : 1;
    return { count, dmgMult };
  }

  // ------------------------------------------------------------------- helpers
  nearestEnemy(x, y, maxR) { return this.hash.nearest(x, y, maxR, (e) => !e.dead); }
  enemiesInRadius(x, y, r) {
    const out = [];
    const list = this.hash.query(x, y, r);
    for (const e of list) if (!e.dead && dist2(e.x, e.y, x, y) <= r * r) out.push(e);
    return out;
  }
  hpScale() { const m = this.overrunMinutes(); return (1 + (this.elapsed / 60) * CONFIG.difficulty.hpPerMin) * (1 + this.player.curse * 0.2) * this.stageDiff.hp * (1 + 0.45 * m + 0.10 * m * m); }
  speedScale() { return (1 + (this.elapsed / 60) * CONFIG.difficulty.speedPerMin) * (1 + this.player.curse * 0.1); }
  dmgScale() { const m = this.overrunMinutes(); return (1 + (this.elapsed / 60) * CONFIG.difficulty.damagePerMin) * this.stageDiff.dmg * (1 + 0.20 * m + 0.05 * m * m); }
  // Overrun's premium purse: grows with the minutes survived, but sub-linearly
  // against the hp curve above, so the phase pays yet self-limits — the horde
  // always wins the race eventually.
  goldScale() { const m = this.overrunMinutes(); return 1 + 0.30 * m + 0.05 * m * m; }

  aimAngle() { return this.player.aimAngle; }

  // Base per-projectile damage: weapon damage × player mult × element factor.
  baseDamage(base) {
    const el = getElement(this.player.element);
    const f = (el && el.dmgFactor) || 1;
    return base * this.player.damageMult * f;
  }

  // -------------------------------------------------------------- particles
  particles_spark(x, y, color, n = 4) {
    n = Math.round(n * Settings.particleScale);
    if (n <= 0 || this.particles.count > 1400) return;
    for (let i = 0; i < n; i++) {
      const pt = this.particles.spawn();
      const a = this.rng.angle(); const s = this.rng.range(40, 190);
      pt.kind = 'spark'; pt.x = x; pt.y = y;
      pt.vx = Math.cos(a) * s; pt.vy = Math.sin(a) * s;
      pt.life = this.rng.range(0.2, 0.45); pt.maxLife = pt.life;
      pt.color = color; pt.size = this.rng.range(2, 4);
    }
  }
  particles_burst(x, y, color, n = 10) {
    n = Math.round(n * Settings.particleScale);
    if (n <= 0 || this.particles.count > 1400) return;
    for (let i = 0; i < n; i++) {
      const pt = this.particles.spawn();
      const a = this.rng.angle(); const s = this.rng.range(60, 260);
      pt.kind = 'spark'; pt.x = x; pt.y = y;
      pt.vx = Math.cos(a) * s; pt.vy = Math.sin(a) * s;
      pt.life = this.rng.range(0.3, 0.7); pt.maxLife = pt.life;
      pt.color = color; pt.size = this.rng.range(2, 5);
    }
  }
  // The dying sprite pops: expands, flashes white and fades (drawn in render).
  _deathPop(e) {
    if (this.particles.count > 1400) return;
    const pt = this.particles.spawn();
    pt.kind = 'pop';
    pt.x = e.x; pt.y = e.y; pt.vx = 0; pt.vy = 0;
    pt.sprite = e.sprite;
    pt.size = e.r * 2.9;             // matches the live sprite's target height
    pt.life = 0.24; pt.maxLife = 0.24;
    pt.color = e.tint;
  }
  particles_ring(x, y, r, color) {
    const pt = this.particles.spawn();
    pt.kind = 'ring'; pt.x = x; pt.y = y; pt.r = 6; pt.maxR = r;
    pt.life = 0.35; pt.maxLife = 0.35; pt.color = color; pt.size = 4;
  }
  _damageNumber(x, y, val, crit, e) {
    if (!Settings.damageNumbers) return;
    // Rapid hits on one target merge into a single climbing number. At horde
    // scale a per-hit spawn stacks dozens of small numbers into an illegible
    // drizzle; one growing figure per enemy reads at a glance and costs less.
    // The eRef guard makes pool recycling safe: a particle reused for anything
    // else changes kind or eRef, and a recycled ENEMY clears _numPt on spawn.
    const prev = e && e._numPt;
    if (prev && !prev.dead && prev.kind === 'text' && prev.eRef === e) {
      prev.val += val;
      prev.text = '' + Math.round(prev.val);
      prev.crit = prev.crit || !!crit;
      prev.color = prev.crit ? '#ffd15c' : '#ffffff';
      prev.size = prev.crit ? 22 : 16;
      const life = prev.crit ? 0.8 : 0.6;
      prev.life = Math.max(prev.life, life);
      prev.maxLife = Math.max(prev.maxLife, prev.life);
      return;
    }
    if (this.particles.count > 1400) return;
    const pt = this.particles.spawn();
    pt.kind = 'text'; pt.x = x + this.rng.range(-6, 6); pt.y = y;
    pt.vx = this.rng.range(-14, 14); pt.vy = -46;
    pt.life = crit ? 0.8 : 0.6; pt.maxLife = pt.life;
    pt.val = val; pt.crit = !!crit; pt.eRef = e || null;
    pt.text = '' + val; pt.color = crit ? '#ffd15c' : '#ffffff';
    pt.size = crit ? 22 : 16;
    if (e) e._numPt = pt;
  }
  _updateParticles(dt) {
    for (const pt of this.particles.active) {
      pt.life -= dt;
      if (pt.life <= 0) { pt.dead = true; continue; }
      if (pt.kind === 'ring') { pt.r += (pt.maxR - pt.r) * Math.min(1, dt * 10); }
      else { pt.x += pt.vx * dt; pt.y += pt.vy * dt; pt.vx *= 0.92; pt.vy *= 0.92; if (pt.kind === 'text') pt.vy += 40 * dt; }
    }
  }

  // ----------------------------------------------------------------- death
  _summary(won) {
    const p = this.player;
    const dmg = Object.entries(this.damageByWeapon || {}).sort((a, b) => b[1] - a[1]);
    const totalDamage = dmg.reduce((s, [, v]) => s + v, 0);
    return {
      character: p.char.id,
      stage: this.stage.name,
      stageId: this.stageId,
      nodeId: this.node ? this.node.nodeId : null,
      nodeName: this.node ? this.node.nodeName : null,
      nodeType: this.node ? this.node.nodeType : null,
      threat: this.threat || 0,
      seed: this.seed,
      gauntlet: !!(this.node && this.node.gauntlet),
      gauntletWeek: (this.node && this.node.gauntletWeek) || 0,
      seconds: Math.floor(this.elapsed),
      duration: this.duration,
      kills: this.kills,
      level: p.level,
      gold: this.gold,
      element: p.element,
      bestCombo: this.bestCombo,
      totalDamage,
      topWeapons: dmg.slice(0, 3).map(([id, v]) => ({ id, dmg: v, pct: totalDamage ? v / totalDamage : 0 })),
      won: !!won,
      overrun: this.overrun,
      overrunSeconds: this.overrun ? Math.max(0, Math.floor(this.elapsed - this.overrunStart)) : 0,
    };
  }
  _handleDeath() {
    const p = this.player;
    // Dying during the Reaper phase still counts as surviving the stage.
    if (this.reaperSpawned) { this._win(); return; }
    if (p.reviveCount > 0) {
      p.reviveCount--;
      this._revive();
      return;
    }
    // Out of revives: offer one rewarded-ad revive per run (native builds only —
    // onOfferRevive is unset on the web, so this path never triggers there).
    if (this.onOfferRevive && !this._adReviveOffered) {
      this._adReviveOffered = true;
      this.state = 'reviveoffer';   // sim frozen while the offer screen is up
      this.onOfferRevive();
      return;
    }
    this.finishDeath();
  }

  _revive() {
    const p = this.player;
    p.hp = p.maxHp * 0.5;
    p.iframes = 2.5;
    this._screenNuke();
    this.setAnnounce('REVIVED', 2.5);
    Platform.haptic('heavy');
  }

  // Rewarded revive granted → back into the fight.
  adRevive() {
    if (this.state !== 'reviveoffer') return;
    this._revive();
    this.state = 'playing';
  }

  // Offer declined / ad failed → the run truly ends.
  finishDeath() {
    this.state = 'dead';
    Audio.death();
    Platform.haptic('heavy');
    if (this.onGameOver) this.onGameOver(this._summary(false));
  }

  // ----------------------------------------------------------------- render
  render() { renderScene(this); }
}

// -------- pooled-object reset helpers --------
// The single source of an enemy's default state — used both to build a fresh
// pool object and to scrub a recycled one, so the two are identical by
// construction.
//
// This matters more than it looks: spawnEnemy() and _spawnBoss() are separate
// hand-written field copies over the SAME pool. Any optional field one of them
// forgets leaks out of a recycled corpse into whatever spawns next. That
// shipped — bosses were inheriting `shardBurst` (firing 8 bullets on death),
// `ghost` (rendering at 55% alpha) and `shootCd` (a shooter boss's fire rate
// decided by whichever corpse it recycled, which also broke seeded
// determinism). Clear every def-authored field here and neither path can leak.
function resetEnemy(e) {
  e.dead = false; e.hitMap = null;
  // death behaviors
  e.splitInto = null; e.splitCount = 0;
  e.explodeOnDeath = false; e.explodeColor = '#ff8a3d';
  e.shardBurst = 0;
  // behavior-specific
  e.shootCd = 0; e.orbitPref = 0; e.ghost = false; e.blinkT = null;
  e.dashing = false; e.chargeT = 0;
  // status effects
  e.burnT = 0; e.burnDps = 0;
  e.poisonT = 0; e.poisonDps = 0; e.poisonStacks = 0;
  e.slowT = 0; e.slowMult = 1; e.freezeT = 0;
  // flags & presentation
  e.boss = false; e.elite = false; e.super = false; e.finale = false; e.noContact = false;
  e.tintGlow = null; e.spriteScale = 1; e.name = null;
  e.bossState = null; e.bossUpdate = null; e.killAch = null; e.cry = null;
  return e;
}
function resetProjectile(p) {
  p.hitIds = null; p.hitMap = null; p.returning = false;
}
function enemyDot(game, amount) {
  // DoT damage that does NOT re-trigger element effects.
  this.hp -= amount;
  if (this.hp <= 0) game._killEnemy(this);
}
